import {useContext} from "react";
import {ToastContext, UserContext} from "../Contexts";
import {
    AdminSetPasswordRequest,
    AdminUserCreateRequest, AdminUserUpdateRequest, AdminUserNameItem,
    ToastFailureMessage, ToastSuccessMessage,
    UserDataContext, UserFilterRequest, UserFilterResponse, UserResponse
} from "../Models/Models";
import HttpRepository from "./HttpRepository";
import {Constants} from "../Models/Constants";
import {useNavigate} from "react-router-dom";
import { AxiosError } from "axios";

export function UserService() {
    let {setToast}: any = useContext(ToastContext);
    let {setCurrentUser}: any = useContext(UserContext);
    let navigate = useNavigate();

    async function login(email: string, password: string): Promise<void> {
        try {
            await HttpRepository.login(email, password);
            setToast({ message: ToastSuccessMessage.loggedIn });
        } catch (err) {
            const error = err as AxiosError;

            // Network error -> interceptor already shows toast
            if (!error.response) {
                throw error;
            }

            // Wrong credentials
            if (error.response.status === 401) {
                setToast({ message: ToastFailureMessage.incorrectCredentials });
                throw error;
            }

            // Blocked account
            if (error.response.status === 403) {
                setToast({ message: ToastFailureMessage.accountBlocked });
                throw error;
            }

            // Other server errors -> let interceptor handle (redirect/toast)
            throw error;
        }
    }

    async function filterUsers(request: UserFilterRequest): Promise<UserFilterResponse> {
        try {
            return (await HttpRepository.filterUsers(request)).data;
        } catch (error) {
            setToast({message: ToastFailureMessage.dataNotLoaded});
        }

    }

    async function logout(): Promise<void> {
        try {
            await HttpRepository.logout();
            setCurrentUser(Constants.defaultUserContext)
        } catch (error) {
            setToast({message: ToastFailureMessage.error});
        }
    }

    async function user(): Promise<UserDataContext> {
        try {
            return (await HttpRepository.user()).data;
        } catch (error) {
            setCurrentUser(Constants.defaultUserContext)
        }
    }

    async function getUserById(id: number): Promise<UserResponse> {
        try {
            return (await HttpRepository.getUserById(id)).data;
        } catch (error) {
            setToast({message: ToastFailureMessage.dataNotLoaded});
        }
    }

    async function getAdminUserNames(): Promise<AdminUserNameItem[]> {
        try {
            return (await HttpRepository.getAdminUserNames()).data.data;
        } catch (error) {
            return [];
        }
    }

    async function createUser(request: AdminUserCreateRequest): Promise<void> {
        try {
            await HttpRepository.createUser(request);
            setToast({message: ToastSuccessMessage.userCreated});
            navigate("/user/table");
        } catch (err) {
            const error = err as AxiosError;

            if (error.response?.status === 409) {
                setToast({ message: ToastFailureMessage.emailAlreadyExists });
                return;
            }

            setToast({ message: ToastFailureMessage.dataNotSaved });
        }
    }

    async function updateUser(request: AdminUserUpdateRequest): Promise<void> {
        try {
            await HttpRepository.udpateUser(request);
        } catch (error) {
            setToast({message: ToastFailureMessage.dataNotSaved});
            return;
        }

        setToast({message: ToastSuccessMessage.dataSaved});
        navigate("/user/table");
    }

    async function setPassword(request: AdminSetPasswordRequest): Promise<void> {
        try {
            await HttpRepository.setPassword(request);
        } catch (error) {
            setToast({message: ToastFailureMessage.dataNotSaved});
            return;
        }

        setCurrentUser((prev: any) => ({...prev, isPasswordTemporary: false}));
        setToast({message: ToastSuccessMessage.dataSaved});
        navigate("/home")
    }

    return {login, logout, user, filterUsers, getUserById, getAdminUserNames, createUser, updateUser, setPassword}
}